import Plan from 'buy/Plan';
import { IosCloseButton } from 'components/iOSCloseButton';
import {
  EXTERNAL_SEGMENTATION_SURVEY_STORAGE_KEY,
  REFERRER_STORAGE_KEY
} from 'data/Config';
import { BFForm, BFFormGroup, BFFormInput, BFFormSubmit } from 'form/BFForm';
import { useMobileLayout } from 'hooks/useMobileLayout';
import { useUser } from 'hooks/useUser';
import React from 'react';
import styled from 'styled-components';
import {
  generateClassName,
  getCountryByIP,
  getCurrencyByCountryCode,
  getValueFromLocalStorage,
  isElectronApp,
  isValidEmail,
  pause
} from 'utils';
import { callFunction } from 'utils/FirebaseUtils';
import { reportBFError } from 'utils/Tracker';
import firebase from 'firebase/app';
import 'firebase/auth';

enum FormState {
  Email,
  Password,
  SignUp
}

const Subtitle = styled.div`
  margin-top: 10px;
  margin-bottom: 16px;
  color: #666;
`;

const SignInHeader: React.FC<{ state: FormState }> = ({ state }) => {
  switch (state) {
    case FormState.Password:
      return <>Sign in</>;
    case FormState.SignUp:
      return <>Register</>;
    default:
      return <>Sign in or register</>;
  }
};

const SignInSubtitle: React.FC<{ state: FormState; email: string }> = ({
  state,
  email
}) => {
  switch (state) {
    case FormState.Password:
      return (
        <>
          Type password for account <strong>{email}</strong>
        </>
      );
    case FormState.SignUp:
      return <>Create a new account.</>;
    default:
      return (
        <>
          Try to sign in with your email, a new account will be created if
          needed.
        </>
      );
  }
};

const ButtonLabel: React.FC<{ state: FormState }> = ({ state }) => {
  switch (state) {
    case FormState.Password:
      return <>Sign in</>;
    case FormState.SignUp:
      return <>Register</>;
    default:
      return <>Next</>;
  }
};

const SignInForm: React.FC = () => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState<string | undefined>();
  const [fullname, setFullname] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState<
    string | undefined
  >();
  // const [occupation, setOccupation] = React.useState<string | undefined>();
  // const [occupationError, setOccupationError] = React.useState<
  //   string | undefined
  // >();
  // const [industry, setIndustry] = React.useState<string | undefined>();
  // const [industryError, setIndustryError] = React.useState<
  //   string | undefined
  // >();
  // const [usecase, setUsecase] = React.useState<string | undefined>();
  // const [usecaseError, setUsecaseError] = React.useState<string | undefined>();
  // const [reference, setReference] = React.useState<string | undefined>();
  // const [referenceError, setReferenceError] = React.useState<
  //   string | undefined
  // >();
  // const [audienceSize, setAudienceSize] = React.useState<string | undefined>();
  // const [audienceSizeError, setAudienceSizeError] = React.useState<
  //   string | undefined
  // >();
  const [subscribeToNewsletter, setSubscribeToNewsletter] =
    React.useState(true);

  const [state, setState] = React.useState<FormState>(FormState.Email);
  const [resetPassword, setResetPassword] = React.useState(false);
  const [resetPasswordSent, setResetPasswordSent] = React.useState(false);

  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const fullnameInputRef = React.useRef<HTMLInputElement>(null);
  const passwordInputRef = React.useRef<HTMLInputElement>(null);

  const goNext = React.useCallback(async () => {
    setIsProcessing(true);
    try {
      switch (state) {
        case FormState.Email: {
          if (!isValidEmail(email)) {
            setEmailError('This email is not valid.');
            break;
          }
          const methods: string[] = await firebase
            .auth()
            .fetchSignInMethodsForEmail(email);
          if (methods.length > 0) {
            if (methods.length === 1 && methods[0] === 'password') {
              setState(FormState.Password);
            } else {
              alert(`Methods ${JSON.stringify(methods)} are not supported`);
            }
          } else {
            setState(FormState.SignUp);
          }
          break;
        }
        case FormState.Password: {
          if (password.trim().length < 6) {
            setPasswordError('Password should have at least 6 characters.');
            break;
          }
          const credentials = await firebase
            .auth()
            .signInWithEmailAndPassword(email, password);
          if (credentials.user !== undefined) {
            // onSuccess();
            return;
          } else {
            console.log(credentials);
            throw new Error('Something went wrong');
          }
        }
        case FormState.SignUp: {
          // TODO: all options
          let isInvalid = false;
          if (!isValidEmail(email)) {
            setEmailError('This email is not valid.');
            isInvalid = true;
          }
          if (password.trim().length < 6) {
            setPasswordError('Password should have at least 6 characters.');
            isInvalid = true;
          }
          // if (occupation === undefined || isEmptyOtherOption(occupation)) {
          //   setOccupationError('This is required.');
          //   isInvalid = true;
          // }
          // if (industry === undefined || isEmptyOtherOption(industry)) {
          //   setIndustryError('This is required.');
          //   isInvalid = true;
          // }
          // if (usecase === undefined || isEmptyOtherOption(usecase)) {
          //   setUsecaseError('This is required.');
          //   isInvalid = true;
          // }
          // if (reference === undefined || isEmptyOtherOption(reference)) {
          //   setReferenceError('This is required.');
          //   isInvalid = true;
          // }
          // if (audienceSize === undefined || isEmptyOtherOption(audienceSize)) {
          //   setAudienceSizeError('This is required.');
          //   isInvalid = true;
          // }
          if (isInvalid) {
            setTimeout(() => {
              document.querySelector('.has-error')?.scrollIntoView(true);
            }, 1);
            break;
          }
          const credentials = await firebase
            .auth()
            .createUserWithEmailAndPassword(email, password);
          if (credentials.user !== undefined && credentials.user !== null) {
            const countryCode = await getCountryByIP();
            const currency = await getCurrencyByCountryCode(countryCode);
            const referrer = getValueFromLocalStorage(REFERRER_STORAGE_KEY);
            const nowStr = new Date().toISOString();
            const update = {
              name: fullname.trim(),
              email: credentials.user.email,
              plan: Plan.Free,
              usedDesktopApp: isElectronApp(),
              countryCode,
              currency,
              ref: referrer !== 'direct' ? referrer : null,
              signedUpDate: nowStr,
              lastSignedInDate: nowStr,
              segmentationSurvey:
                getValueFromLocalStorage(
                  EXTERNAL_SEGMENTATION_SURVEY_STORAGE_KEY
                ) === 'true'
                  ? 'skip'
                  : null
              // marketingSurvey: {
              //   occupation,
              //   industry,
              //   usecase,
              //   reference,
              //   audienceSize
              // }
            };
            const ref = firebase
              .database()
              .ref('users/' + credentials.user.uid);
            await ref.update(update);
            if (subscribeToNewsletter) {
              await callFunction('changeSubscriptionToNewsletter', true);
            }
            // onSuccess();
            return;
          } else {
            console.log(credentials);
            throw new Error('Something went wrong');
          }
        }
        default:
        //
      }
    } catch (e) {
      if (e.code !== undefined) {
        switch (e.code) {
          case 'auth/invalid-email':
            setEmailError('This email is invalid.');
            break;
          case 'auth/user-not-found':
            setEmailError('Cannot find this account.');
            break;
          case 'auth/wrong-password':
            setPasswordError('Incorrect password.');
            break;
          case 'auth/user-disabled':
            setEmailError('This account has been disabled.');
            break;
          // case 'auth/too-many-requests':
          //   alert('Too many requests. Please try again.');
          //   break;
          default:
            // Handle all other errors here
            reportBFError(new Error('Auth failed: ' + e.code));
            alert('Something went wrong. Please try again.');
        }
      }
    }
    setIsProcessing(false);
  }, [
    email,
    fullname,
    password,
    // occupation,
    // industry,
    // usecase,
    // reference,
    // audienceSize,
    subscribeToNewsletter,
    state
  ]);

  const focusInputFieldByName = React.useCallback(async (name: string) => {
    await pause(100);
    const input = document.querySelector(`input[name=${name}]`);
    if (input) {
      (input as HTMLInputElement)?.focus();
    } else {
      console.log('Cannot find input with name: ' + name);
    }
  }, []);

  React.useEffect(() => {
    if (state === FormState.Email) {
      focusInputFieldByName('email');
    } else if (state === FormState.Password) {
      focusInputFieldByName('password');
    } else if (state === FormState.SignUp) {
      focusInputFieldByName('fullname');
    }
  }, [state, focusInputFieldByName]);

  const sendResetInstructions = React.useCallback(async () => {
    if (!isValidEmail(email)) {
      setEmailError('This email is not valid.');
      return;
    }
    setIsProcessing(true);
    try {
      await firebase.auth().sendPasswordResetEmail(email);
      setResetPasswordSent(true);
    } catch (err) {
      alert(`Something went wrong. ` + err.toString());
    }
    setIsProcessing(false);
  }, [email]);

  if (resetPassword) {
    if (resetPasswordSent) {
      return (
        <div>
          <h1>Email sent</h1>
          <Subtitle>
            Follow the instruction sent to <strong>{email}</strong> to reset
            your password.
          </Subtitle>
        </div>
      );
    }
    return (
      <div>
        <h1>Reset password</h1>
        <Subtitle>
          Get instructions sent to this email that explain how to reset your
          password.
        </Subtitle>
        <BFForm onSubmit={e => e.preventDefault()}>
          <BFFormInput
            ref={emailInputRef}
            type="email"
            name="email"
            autoComplete="username"
            label="Email address"
            value={email}
            placeholder="e.g. email@example.com"
            onChange={v => {
              setEmail(v);
              setEmailError(undefined);
            }}
            error={emailError}
            preventWhiteSpaces={true}
          />
          <BFFormSubmit>
            <button
              type="button"
              className="large minimal"
              onClick={() => {
                setResetPassword(false);
                setState(FormState.Email);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={sendResetInstructions}
              disabled={isProcessing}
              className="large primary"
            >
              Send
            </button>
          </BFFormSubmit>
        </BFForm>
      </div>
    );
  }

  return (
    <div>
      <h1>
        <SignInHeader state={state} />
      </h1>
      <Subtitle>
        <SignInSubtitle state={state} email={email} />
      </Subtitle>
      <BFForm onSubmit={e => e.preventDefault()}>
        {(state === FormState.Email || state === FormState.SignUp) && (
          <BFFormInput
            ref={emailInputRef}
            type="email"
            name="email"
            autoComplete="username"
            label="Email address"
            value={email}
            placeholder="e.g. email@example.com"
            onChange={v => {
              setEmail(v);
              setEmailError(undefined);
            }}
            error={emailError}
            preventWhiteSpaces={true}
          />
        )}
        {state === FormState.SignUp && (
          <BFFormInput
            ref={fullnameInputRef}
            type="text"
            name="fullname"
            autoComplete="name"
            label="Full name"
            value={fullname}
            placeholder="e.g. Joe Doe"
            onChange={setFullname}
          />
        )}
        {(state === FormState.Password || state === FormState.SignUp) && (
          <BFFormInput
            ref={passwordInputRef}
            type="password"
            name="password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={v => {
              setPassword(v);
              setPasswordError(undefined);
            }}
            error={passwordError}
            preventWhiteSpaces={true}
            disabled={isProcessing}
          />
        )}
        {state === FormState.SignUp && (
          <>
            <BFFormGroup style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="newsletter"
                checked={subscribeToNewsletter}
                onChange={e => setSubscribeToNewsletter(e.target.checked)}
              />
              <label
                htmlFor="newsletter"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setSubscribeToNewsletter(!subscribeToNewsletter);
                }}
              >
                Receive email updates
              </label>
            </BFFormGroup>
            {/* <hr />
            <BFFormInput
              type="select"
              name="occupation"
              label="Occupation"
              value={occupation}
              options={[
                'Event organizer',
                'Musician/Producer',
                'Small business owner',
                'Marketer',
                'Designer'
              ]}
              allowCustomOption={true}
              onChange={setOccupation}
              error={occupationError}
            />
            <BFFormInput
              type="select"
              name="industry"
              label="Industry"
              value={industry}
              options={[
                'Entertainment',
                'Marketing & Advertising',
                'Retail',
                'Technology',
                'Hospitality',
                'Non-profit'
              ]}
              allowCustomOption={true}
              onChange={setIndustry}
              error={industryError}
            />
            <BFFormInput
              type="select"
              name="usecase"
              label="Primary use case"
              value={usecase}
              options={[
                'Promoting events',
                'Sharing music productions',
                'Marketing products/services',
                'Showcasing design projects',
                'Podcast promotions'
              ]}
              allowCustomOption={true}
              onChange={setUsecase}
              error={usecaseError}
            />
            <BFFormInput
              type="select"
              name="reference"
              label="How did you hear about Beatflyer?"
              value={reference}
              options={[
                'Social media',
                'Referral from a friend',
                'Online advertisement',
                'Search engine',
                'Blog/Article'
              ]}
              allowCustomOption={true}
              onChange={setReference}
              error={referenceError}
            />
            <BFFormInput
              type="select"
              name="audience"
              label="Roughly, how big is your audience?"
              value={audienceSize}
              options={[
                'Less than 500',
                '501 - 2,000',
                '2,001 - 5,000',
                '5,001 - 10,000',
                'More than 10,000'
              ]}
              onChange={setAudienceSize}
              error={audienceSizeError}
            /> */}
          </>
        )}
        <BFFormSubmit>
          {state === FormState.SignUp && (
            <button
              type="button"
              className="large minimal"
              onClick={() => setState(FormState.Email)}
            >
              Back
            </button>
          )}
          {state === FormState.Password && (
            <button
              type="button"
              className="large minimal"
              onClick={() => setResetPassword(true)}
            >
              Forgot the password?
            </button>
          )}
          <button
            type="submit"
            onClick={goNext}
            disabled={isProcessing}
            className="large primary"
          >
            <ButtonLabel state={state} />
          </button>
        </BFFormSubmit>
        {state !== FormState.Password && (
          <footer>
            By signing up, you agree to our{' '}
            <a href="./tos.html" target="_blank">
              Terms of Service
            </a>
            .
          </footer>
        )}
      </BFForm>
    </div>
  );
};

export const SignIn: React.FC<{ onClose: (success: boolean) => void }> = ({
  onClose
}) => {
  const isWebApp = useMobileLayout();
  const user = useUser();

  React.useEffect(() => {
    if (user) {
      onClose(true);
    }
  }, [user, onClose]);

  // if (user) {
  //   return null;
  // }

  const noFirebase = firebase === undefined || firebase.auth === undefined;

  return (
    <div
      className={generateClassName('overlay', {
        opaque: true
        // fullWidth: props.fullWidth,
        // fullHeight: true
      })}
      style={{ backgroundColor: '#F0F0F0' }}
    >
      <div className="overlay-content">
        {noFirebase ? (
          <> Firebase not available. Please try again.</>
        ) : (
          <SignInForm />
        )}
      </div>

      {isWebApp ? (
        <IosCloseButton onClick={() => onClose(false)} />
      ) : (
        <button
          className="tertiary close close--figma"
          onClick={() => onClose(false)}
        />
      )}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/signin/SignIn.tsx